/**
 * The executive dashboard: the response shapes of the dashboard routes, the
 * search parameters of the page, and the small helpers the tabs share.
 *
 * Field names follow the server. The browser reads them; it calculates no
 * metric and decides no permission.
 */

// --- Program ---------------------------------------------------------------

export type Phase = "unset" | "implementing" | "operating";
export type AssessmentPath = "c3pao" | "self_assessment" | "internal_evaluation";
export type Activity = "active" | "paused" | "archived";
export type Workstream = "scope" | "initial_review" | "implementation" | "documents" | "validation" | "handover" | "improvement";
export type DeliverableState = "not_started" | "in_progress" | "blocked" | "in_review" | "complete" | "excluded";
export type Responsibility = "customer" | "consultant" | "provider" | "shared";
export type Schedule = "no_target" | "on_schedule" | "at_risk" | "overdue" | "complete";
export type Health = "action_required" | "attention_needed" | "on_track" | "status_unknown";
export type Quality = "current" | "stale" | "unavailable" | "incomplete";
export type FrameworkKey = "cmmc-l1" | "cmmc-l2" | "hipaa";

export interface Program {
  readonly id: string;
  readonly tenantId: string;
  readonly frameworkKey: FrameworkKey;
  readonly frameworkName: string;
  readonly frameworkShortName: string;
  readonly catalogIds: readonly string[];
  readonly systemId: string | null;
  readonly systemName: string | null;
  readonly phase: Phase;
  readonly phaseLabel: string;
  readonly phaseSince: string | null;
  readonly assessmentPath: AssessmentPath | null;
  readonly assessmentPathLabel: string | null;
  readonly ownerUserId: string | null;
  readonly ownerName: string | null;
  readonly startOn: string | null;
  readonly targetCompletionOn: string | null;
  readonly originalTargetCompletionOn: string | null;
  readonly targetAssessmentOn: string | null;
  readonly activity: Activity;
  readonly workspaceState: "active" | "paused" | "not_adopted";
  readonly roadmapRevision: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface PhaseEvent {
  readonly id: string;
  readonly phase: Phase;
  readonly previousPhase: string;
  readonly effectiveOn: string;
  readonly reason: string;
  readonly source: string;
  readonly checklist: Record<string, unknown>;
  readonly remainingWork: readonly unknown[];
  readonly actorName: string | null;
  readonly createdAt: string;
}

export interface ProgramUpdate {
  readonly id: string;
  readonly body: string;
  readonly authorName: string | null;
  readonly createdAt: string;
  readonly predatesChange: boolean;
}

// --- Roadmap ---------------------------------------------------------------

export interface SourceLink {
  readonly type: "task" | "document" | "assessment" | "monitoring_plan" | "requirement" | "evidence" | "engagement" | "scope" | "risk_analysis" | "inventory" | "ssp";
  readonly id: string;
  readonly title?: string | undefined;
}

export interface Deliverable {
  readonly id: string;
  readonly templateKey: string | null;
  readonly workstream: Workstream;
  readonly workstreamLabel: string;
  readonly title: string;
  readonly acceptance: string;
  readonly ownerUserId: string | null;
  readonly ownerName: string | null;
  readonly responsibility: Responsibility;
  readonly state: DeliverableState;
  readonly stateLabel: string;
  readonly blockedReason: string;
  readonly exclusionReason: string;
  readonly completionNote: string;
  readonly sourceLinks: readonly SourceLink[];
  readonly plannedStartOn: string | null;
  readonly plannedDueOn: string | null;
  readonly originalDueOn: string | null;
  readonly rescheduleReason: string;
  readonly completedOn: string | null;
  readonly firstCompletedOn: string | null;
  readonly dependsOn: readonly string[];
  readonly baselineRevision: number;
  readonly removedRevision: number | null;
  readonly sort: number;
  readonly sourceMet: boolean | null;
  readonly sourceNote: string | null;
  readonly blockedBy: readonly string[];
  readonly schedule: Schedule;
  readonly scheduleNote: string;
}

export interface RoadmapSummary {
  readonly total: number;
  readonly complete: number;
  readonly excluded: number;
  readonly blocked: number;
  readonly overdue: number;
  readonly atRisk: number;
  readonly percent: number | null;
  readonly currentMilestone: string | null;
  readonly nextDue: { title: string; dueOn: string; ownerName: string | null } | null;
  readonly byWorkstream: readonly { workstream: Workstream; label: string; total: number; complete: number }[];
}

export interface RoadmapRevision {
  readonly revision: number;
  readonly reason: string;
  readonly added: number;
  readonly removed: number;
  readonly changed: number;
  readonly actorName: string | null;
  readonly createdAt: string;
}

export interface DeliverableInput {
  readonly title?: string | undefined;
  readonly workstream?: Workstream | undefined;
  readonly acceptance?: string | undefined;
  readonly ownerUserId?: string | null | undefined;
  readonly responsibility?: Responsibility | undefined;
  readonly state?: DeliverableState | undefined;
  readonly blockedReason?: string | undefined;
  readonly exclusionReason?: string | undefined;
  readonly completionNote?: string | undefined;
  readonly sourceLinks?: readonly SourceLink[] | undefined;
  readonly plannedStartOn?: string | null | undefined;
  readonly plannedDueOn?: string | null | undefined;
  readonly rescheduleReason?: string | undefined;
  readonly dependsOn?: readonly string[] | undefined;
  readonly reason?: string | undefined;
}

export interface TemplatePreview {
  readonly deliverables: readonly {
    readonly key: string;
    readonly workstream: Workstream;
    readonly workstreamLabel: string;
    readonly title: string;
    readonly acceptance: string;
    readonly source: SourceLink["type"] | null;
    readonly exists: boolean;
  }[];
  readonly adds: number;
}

// --- Metrics ---------------------------------------------------------------

export interface Metric {
  readonly key: string;
  readonly label: string;
  readonly definition: string;
  readonly value: number | null;
  readonly numerator: number | null;
  readonly denominator: number | null;
  readonly text: string;
  readonly scope: string;
  readonly asOf: string;
  readonly sourceRevision: string;
  readonly quality: Quality;
  readonly qualityNote: string;
  readonly drilldown: string | null;
}

export interface Reason {
  readonly code: string;
  readonly severity: "action" | "attention" | "info";
  readonly title: string;
  readonly effect: string;
  readonly ownerName: string | null;
  readonly dueOn: string | null;
  readonly count: number;
  readonly link: string | null;
  readonly priority: string;
  readonly affected: readonly string[];
}

export interface ImplementationMetrics {
  readonly applicable: number;
  readonly verified: number;
  readonly percent: number | null;
  readonly text: string;
  readonly states: { verified: number; awaitingVerification: number; inProgress: number; notStarted: number; applicabilityUnresolved: number };
  readonly approvedExclusions: number;
  readonly unitLabel: string;
  readonly assessments: readonly { id: string; name: string; systemName: string; type: string }[];
  readonly quality: Quality;
  readonly qualityNote: string;
  readonly catalogVersion: string | null;
}

export interface MonitoringMetrics {
  readonly available: boolean;
  readonly note: string;
  readonly plans: readonly { id: string; name: string; status: string; ownerName: string | null }[];
  readonly coverage: { covered: number; applicable: number; configurationGaps: number };
  readonly backlog: { overdue: number; dueNow: number; dueSoon: number };
  readonly period: { start: string; end: string; due: number; completed: number; completedLate: number; onTime: number; waived: number; onTimeRate: number | null; completionRate: number | null };
  readonly effectiveness: { effective: number; partiallyEffective: number; ineffective: number; notApplicable: number; notTested: number; entries: number };
  readonly unresolvedIneffective: number;
  readonly oldestOverdue: { entry: string; dueOn: string; ownerName: string | null } | null;
}

export interface DocumentMetrics {
  readonly effective: number;
  readonly overdueReviews: number;
  readonly missingReviewDate: number;
  readonly missingOwner: number;
  readonly pendingApprovals: number;
  readonly draftsOfEffective: number;
  readonly dueSoon: number;
  readonly oldestOverdue: { title: string; nextReviewDate: string; ownerName: string | null } | null;
}

export interface RiskMetrics {
  readonly openFindings: number;
  readonly findingsWithoutOwner: number;
  readonly significantRisks: number;
  readonly openActions: number;
  readonly overdueActions: number;
  readonly conditionalPoam: { open: number; closeoutDeadline: string | null };
  readonly acceptancesExpiring: number;
  readonly acceptancesExpired: number;
  readonly openTasks: number;
  readonly overdueTasks: number;
}

export interface EvidenceMetrics {
  readonly available: boolean;
  readonly items: number;
  readonly outsideReviewPeriod: number;
  readonly needsUpdate: number;
  readonly unreviewedLinks: number;
}

export interface SspMetrics {
  readonly exists: boolean;
  readonly documentId: string | null;
  readonly title: string | null;
  readonly state: string | null;
  readonly versionLabel: string | null;
  readonly nextReviewDate: string | null;
  readonly sourceCheckedAt: string | null;
  readonly sourceChangesPending: boolean | null;
}

export interface WorkforceMetrics {
  readonly available: boolean;
  readonly trainingOverdue: number;
  readonly trainingDueSoon: number;
  readonly acknowledgmentsOutstanding: number;
  readonly employees: number;
}

export interface AssessmentStatus {
  readonly path: string;
  readonly label: string;
  readonly recorded: string;
  readonly recordedLabel: string;
  readonly source: string | null;
  readonly confirmedOn: string | null;
  readonly statusDate: string | null;
  readonly conditionalStatusDate: string | null;
  readonly closeoutDeadline: string | null;
  readonly officialScore: { value: number; source: string; reportedOn: string } | null;
  readonly calculatedScore: number | null;
  readonly affirmation: { reference: string; source: string; date: string | null } | null;
  readonly nextObligation: { label: string; dueOn: string | null; basis: string } | null;
  readonly engagement: { id: string; name: string; state: string; countdownText: string; kind: string } | null;
  readonly latestAssessment: { id: string; name: string; type: string; state: string; finalizedAt: string | null } | null;
  readonly note: string;
}

export interface HipaaMetrics {
  readonly available: boolean;
  readonly entityRole: string | null;
  readonly riskAnalysis: { id: string; state: string; createdAt: string; nextReviewDate: string | null; overdue: boolean } | null;
  readonly evaluationEntries: { total: number; reviewed: number; overdue: number };
  readonly safeguards: { required: number; addressable: number; decided: number; implemented: number };
  readonly agreements: { active: number; expiring: number; associatesWithoutAgreement: number };
}

export interface Freshness {
  readonly calculatedAt: string;
  readonly monitoringSweep: { lastSuccessAt: string | null; lastFailureAt: string | null; stale: boolean };
  readonly thresholdDays: number;
  readonly failedSources: readonly string[];
  readonly lastActivityAt: string | null;
  readonly samplesExcluded: number;
}

export interface ProgramMetrics {
  readonly methodVersion: string;
  readonly today: string;
  readonly calculatedAt: string;
  readonly implementation: ImplementationMetrics;
  readonly monitoring: MonitoringMetrics;
  readonly documents: DocumentMetrics;
  readonly risk: RiskMetrics;
  readonly evidence: EvidenceMetrics;
  readonly ssp: SspMetrics | null;
  readonly workforce: WorkforceMetrics;
  readonly assessment: AssessmentStatus;
  readonly hipaa: HipaaMetrics | null;
  readonly freshness: Freshness;
  readonly scope: { systemIds: readonly string[]; systemNames: readonly string[]; scopeRevision: number | null; scopeApproved: boolean; catalogVersion: string | null };
  readonly metrics: readonly Metric[];
}

// --- View ------------------------------------------------------------------

export interface Card {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly detail: string;
  readonly condition: "action" | "attention" | "ok" | "unknown" | "neutral";
  readonly link: string | null;
}

export interface UpcomingItem {
  readonly kind: string;
  readonly title: string;
  readonly dueOn: string;
  readonly ownerName: string | null;
  readonly link: string | null;
}

export interface ChecklistItem {
  readonly key: string;
  readonly title: string;
  readonly ready: boolean;
  readonly detail: string;
  readonly link: string | null;
}

export interface HealthView {
  readonly condition: Health;
  readonly label: string;
  readonly topReason: string;
  readonly quality: "current" | "stale" | "incomplete";
  readonly qualityNote: string;
  readonly reasons: readonly Reason[];
}

export type DashboardViewKind = "implementation" | "ongoing";

/** The executive roadmap: milestones with a state, a status, and a count a person sets by hand. */
export type RoadmapStage = "implementation" | "ongoing";
export type RoadmapState = "not_started" | "in_progress" | "complete";
export type RoadmapHealth = "on_track" | "at_risk" | "action_required";
export type RoadmapCycle = "none" | "monthly" | "quarterly" | "annual";
export const ROADMAP_STATES: readonly RoadmapState[] = ["not_started", "in_progress", "complete"];
export const ROADMAP_HEALTHS: readonly RoadmapHealth[] = ["on_track", "at_risk", "action_required"];
export const ROADMAP_CYCLES: readonly RoadmapCycle[] = ["none", "monthly", "quarterly", "annual"];

export interface RoadmapCycleResult {
  readonly cycleLabel: string;
  readonly state: RoadmapState;
  readonly stateLabel: string;
  readonly health: RoadmapHealth | null;
  readonly healthLabel: string | null;
  readonly progressDone: number | null;
  readonly progressTotal: number | null;
  readonly note: string;
}

export interface RoadmapItem {
  readonly id: string;
  readonly stage: RoadmapStage;
  readonly category: string;
  readonly position: number;
  readonly templateKey: string | null;
  readonly title: string;
  readonly measure: string;
  readonly state: RoadmapState;
  readonly stateLabel: string;
  readonly health: RoadmapHealth | null;
  readonly healthLabel: string | null;
  readonly progressDone: number | null;
  readonly progressTotal: number | null;
  readonly progressUnit: string;
  readonly progressText: string;
  readonly note: string;
  readonly setAt: string | null;
  readonly setByName: string | null;
  readonly needsReview: boolean;
  readonly cycle: RoadmapCycle;
  readonly cycleLabel: string;
  readonly previous: RoadmapCycleResult | null;
  readonly ownerUserId: string | null;
  readonly ownerName: string | null;
  readonly targetOn: string | null;
  readonly history: readonly { state: RoadmapState; stateLabel: string; health: RoadmapHealth | null; healthLabel: string | null; progressText: string; note: string; cycleLabel: string; setAt: string; setByName: string | null }[];
}

export interface RoadmapCategory {
  readonly stage: RoadmapStage;
  readonly category: string;
  readonly total: number;
  readonly complete: number;
  readonly inProgress: number;
  readonly notStarted: number;
  readonly health: RoadmapHealth | null;
  readonly current: readonly { id: string; title: string; progressText: string }[];
  readonly decision: { id: string; title: string; health: RoadmapHealth; issue: string } | null;
  readonly next: { id: string; title: string; ownerName: string | null; targetOn: string | null } | null;
}

export interface ExecutiveRoadmap {
  readonly items: readonly RoadmapItem[];
  readonly stages: readonly { stage: RoadmapStage; label: string; total: number; complete: number; categories: readonly RoadmapCategory[] }[];
  readonly movement: { since: string; sinceUpdate: boolean; completed: number; toRed: number; text: string };
  readonly missing: readonly { key: string; stage: RoadmapStage; title: string }[];
  readonly canEdit: boolean;
  readonly stateLabels: Record<RoadmapState, string>;
  readonly healthLabels: Record<RoadmapHealth, string>;
  readonly healthMeanings: Record<RoadmapHealth, string>;
  readonly stageLabels: Record<RoadmapStage, string>;
  readonly cycleLabels: Record<RoadmapCycle, string>;
  readonly categories: Record<RoadmapStage, readonly string[]>;
  readonly reviewDays: number;
  readonly today: string;
}

export interface DashboardView {
  readonly program: Program;
  readonly view: DashboardViewKind;
  readonly defaultView: DashboardViewKind;
  readonly context: { organizationName: string; scope: string; framework: string; phase: string; ownerName: string | null; refreshedAt: string; calculatedAt: string; timeZone: string };
  readonly summary: readonly string[];
  readonly cards: readonly Card[];
  readonly health: HealthView;
  readonly schedule: { schedule: Schedule; label: string; note: string };
  readonly assessment: AssessmentStatus;
  readonly roadmap: { summary: RoadmapSummary; deliverables: readonly Deliverable[]; revisions: readonly RoadmapRevision[] } | null;
  readonly executiveRoadmap: ExecutiveRoadmap;
  readonly attention: { items: readonly Reason[]; total: number };
  readonly next: { windowDays: number; items: readonly UpcomingItem[]; total: number };
  readonly metrics: ProgramMetrics;
  readonly definitions: readonly Metric[];
  readonly updates: readonly ProgramUpdate[];
  readonly phaseEvents: readonly PhaseEvent[];
  readonly handover: { ready: boolean; items: readonly ChecklistItem[]; suggested: boolean } | null;
  readonly access: { canManage: boolean; drilldowns: Record<string, boolean>; sourceDetail: boolean; note: string };
  readonly notes: readonly string[];
}

export interface OverviewCard {
  readonly program: Program;
  readonly condition: Health;
  readonly conditionLabel: string;
  readonly topReason: string;
  readonly implementation: string;
  readonly assessment: string;
  readonly nextMilestone: { title: string; dueOn: string | null } | null;
  readonly lastUpdate: string | null;
  readonly attention: number;
}

export interface ProgramsResponse {
  readonly programs: readonly Program[];
  readonly cards: readonly OverviewCard[];
  readonly archived: number;
}

// --- Trend -----------------------------------------------------------------

export interface Ratio {
  readonly numerator: number;
  readonly denominator: number;
  readonly percent: number | null;
}

export interface SnapshotMetrics {
  readonly implementationVerified: Ratio;
  readonly roadmapDelivery: Ratio | null;
  readonly reviewBacklog: number | null;
  readonly onTimeRate: Ratio | null;
  readonly documentReviewsOverdue: number;
  readonly openFindings: number;
  readonly overdueActions: number;
  readonly health: string;
}

export interface SnapshotVersions {
  readonly methodVersion: string;
  readonly catalogVersion: string | null;
  readonly scopeRevision: number | null;
  readonly roadmapRevision: number;
  readonly phase: string;
  readonly monitoringWatermark: string | null;
}

export interface TrendPoint {
  readonly takenOn: string;
  readonly metrics: SnapshotMetrics;
  readonly versions: SnapshotVersions;
  readonly annotation: string | null;
}

export interface TrendResponse {
  readonly historyStartsOn: string | null;
  readonly points: readonly TrendPoint[];
}

// --- People ----------------------------------------------------------------

export interface PersonOption {
  readonly userId: string;
  readonly displayName: string;
}

/** The people list, read with tolerance for the two shapes the server has used. */
export function peopleOf(payload: unknown): PersonOption[] {
  const rows: unknown = Array.isArray(payload) ? payload : (payload as { people?: unknown } | null)?.people;
  if (!Array.isArray(rows)) return [];
  const out: PersonOption[] = [];
  for (const row of rows as Record<string, unknown>[]) {
    const userId = typeof row["userId"] === "string" ? row["userId"] : typeof row["id"] === "string" ? row["id"] : null;
    const displayName = typeof row["displayName"] === "string" ? row["displayName"] : typeof row["name"] === "string" ? row["name"] : null;
    if (userId !== null) out.push({ userId, displayName: displayName ?? userId });
  }
  return out;
}

// --- Search parameters ------------------------------------------------------

export type DashboardTab = "overview" | "executive" | "roadmap" | "history" | "setup";
export type WindowDays = "30" | "60" | "90";
export type PeriodDays = "30" | "90" | "180" | "365";
export type TrendDays = "30" | "90" | "180";

export interface DashboardSearch {
  readonly program?: string | undefined;
  readonly tab?: DashboardTab | undefined;
  readonly view?: DashboardViewKind | undefined;
  readonly window?: WindowDays | undefined;
  readonly period?: PeriodDays | undefined;
  readonly trend?: TrendDays | undefined;
  readonly archived?: "yes" | undefined;
}

const oneOf = <T extends string>(value: unknown, allowed: readonly T[]): T | undefined =>
  typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : undefined;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Read the dashboard search parameters. An unknown value is dropped. */
export function parseDashboardSearch(search: Record<string, unknown>): DashboardSearch {
  return {
    program: typeof search["program"] === "string" && UUID.test(search["program"]) ? search["program"] : undefined,
    tab: oneOf(search["tab"], ["overview", "executive", "roadmap", "history", "setup"]),
    view: oneOf(search["view"], ["implementation", "ongoing"]),
    window: oneOf(search["window"], ["30", "60", "90"]),
    period: oneOf(search["period"], ["30", "90", "180", "365"]),
    trend: oneOf(search["trend"], ["30", "90", "180"]),
    archived: oneOf(search["archived"], ["yes"]),
  };
}

// --- Labels and helpers -------------------------------------------------------

export const WORKSTREAM_LABEL: Record<Workstream, string> = {
  scope: "Scope and ownership", initial_review: "Initial review", implementation: "Implementation", documents: "Documents and evidence",
  validation: "Validation and assessment", handover: "Operational handover", improvement: "Improvement",
};
export const DELIVERABLE_STATE_LABEL: Record<DeliverableState, string> = {
  not_started: "Not started", in_progress: "In progress", blocked: "Blocked", in_review: "In review", complete: "Complete", excluded: "Excluded",
};
export const RESPONSIBILITY_LABEL: Record<Responsibility, string> = { customer: "Customer", consultant: "Consultant", provider: "Provider", shared: "Shared" };
export const ASSESSMENT_PATH_LABEL: Record<AssessmentPath, string> = { c3pao: "C3PAO assessment", self_assessment: "Self-assessment", internal_evaluation: "Internal evaluation" };
export const SOURCE_LINK_TYPES: readonly SourceLink["type"][] = ["task", "document", "assessment", "monitoring_plan", "requirement", "evidence", "engagement", "scope", "risk_analysis", "inventory", "ssp"];

export { problemText } from "./api.ts";

/** A timestamp as "YYYY-MM-DD HH:MM UTC". */
export function stamp(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") return "never";
  return `${value.slice(0, 16).replace("T", " ")} UTC`;
}

/** The scope of a program, for a heading. */
export function scopeOf(program: Program): string {
  return program.systemName ?? "Whole organization";
}
